import { baseApi } from "../../../core/data/rtk/baseQuery";
import { ApiResponseType } from "../../../core/data/rtk/apiResponseType";
import { NotificationResponseType, Data } from "../types/notificationResponse";
import { NotificationPayloadType } from "../types/notificationRequest";

// Interface for the mark as read payload
interface MarkAsReadPayload {
  notification_id: number;
  center_id: number;
}

export const notificationApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllNotifications: builder.query<
      ApiResponseType<Data>,
      { size?: number; search?: string; page?: number; created?: boolean }
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params?.size) queryParams.append("size", params.size.toString());
        if (params?.search) queryParams.append("search", params.search);
        if (params?.page) queryParams.append("page", params.page.toString());
        if (params?.created)
          queryParams.append("created", params.created.toString());

        return `notifications/get-all?${queryParams.toString()}`;
      },
      providesTags: ["Notifications"],
    }),
    addNotification: builder.mutation<
      ApiResponseType<{ notification: NotificationResponseType }>,
      NotificationPayloadType
    >({
      query: (payload) => ({
        url: "notifications/save",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Notifications"],
    }),
    getNotificationById: builder.query<
      ApiResponseType<{ notification: NotificationResponseType }>,
      { id: string }
    >({
      query: ({ id }) => `notifications/get/${id}`,
      providesTags: (result, error, { id }) => [
        { type: "Notifications", id },
        "Notifications",
      ],
    }),
    updateNotification: builder.mutation<
      ApiResponseType<{ notification: NotificationResponseType }>,
      { id: string; payload: NotificationPayloadType }
    >({
      query: ({ id, payload }) => ({
        url: `notifications/update/${id}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["Notifications"],
    }),
    deleteNotification: builder.mutation<
      ApiResponseType<{ deleted: true }>,
      { id: string }
    >({
      query: ({ id }) => ({
        url: `notifications/delete/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Notifications"],
    }),
    publishNotification: builder.mutation<
      ApiResponseType<{ notification: NotificationResponseType }>,
      { id: string; isPublished: boolean }
    >({
      query: ({ id, isPublished }) => {
        const url = `notifications/${id}/publish/${isPublished}`;
        return {
          url,
          method: "PUT",
        };
      },
      invalidatesTags: ["Notifications"],
    }),
    removeNotificationFiles: builder.mutation<
      ApiResponseType<{ success: boolean }>,
      { id: string; filenames: string[] }
    >({
      query: ({ id, filenames }) => ({
        url: `notifications/${id}/remove-files`,
        method: "POST",
        body: { filenames },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Notifications", id },
        "Notifications",
      ],
    }),
    // New endpoint for marking notifications as read
    markNotificationAsRead: builder.mutation<
      ApiResponseType<{ success: boolean }>,
      MarkAsReadPayload
    >({
      query: (payload) => ({
        url: `notifications/center/mark-as-read`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["Notifications"],
    }),
  }),
});

export const {
  useGetAllNotificationsQuery,
  useAddNotificationMutation,
  useGetNotificationByIdQuery,
  useUpdateNotificationMutation,
  useDeleteNotificationMutation,
  usePublishNotificationMutation,
  useRemoveNotificationFilesMutation,
  useMarkNotificationAsReadMutation,
} = notificationApi;
